import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {Machine} from '@models/machine/machine';
import {MachineEditComponent} from '@components/administration/machine/modals/machine-edit/machine-edit.component';
import {GatewayComponent} from '@components/administration/machine/modals/gateway/gateway.component';
import {cloneDeep} from 'lodash';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {
  MachineDeleteComponent
} from '@components/administration/machine/modals/machine-delete/machine-delete.component';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ModalService} from '@services/modal/modal.service';
import {btou} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-admin-machine-action',
    templateUrl: './machine-action.component.html',
    styleUrls: ['./machine-action.component.scss'],
    standalone: false
})
export class MachineActionComponent implements OnDestroy {

  @Input()
  firebaseUserId: string ='';

  @Input()
  loggedUser: User = new User();

  @Input()
  machine: Machine | any;

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  searchTerm: string = '';

  @Output()
  machineDeleteEvent = new EventEmitter<Machine>();

  @Output()
  machineUpdatedEvent = new EventEmitter<Machine>();

  private COMPONENT_NAME: string = 'MachineActionComponent';
  private DENY_MACHINE_DELETION_HAS_GATEWAYS = 409;
  private DENY_MACHINE_DELETION_IS_TEMPLATE_MACHINE = 417;
  private readonly accessToMachineNotAllowedMessage = '';
  private readonly errorGettingDataMessage = '';
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private modalService: ModalService,
      private router: Router,
      private translate: TranslateService,
      private confirmationDialogService: ConfirmationDialogService,
      private loadingIndicator: LoadingIndicatorService,
      private notification: NotificationService,
      private machineService: MachineService,
      private menuNotificationService: MenuNotificationService,
      private favoriteNotificationService: FavoriteNotificationService) {

    this.accessToMachineNotAllowedMessage = this.translate.instant('You don\'t have access to that machine because it is not part of your machinery groups. Please contact your portal provider for details.');
    this.errorGettingDataMessage = this.translate.instant('Failed to retrieve machine availability information for user.');
  }

  public onDeleteMachine(machine: Machine): void {
    if (!machine || !machine.id) {
      return;
    }

    this.machineService.machineIsAvailableForUser(machine.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAvailable: boolean) => {
        if (!isAvailable) {
          this.notification.error(this.accessToMachineNotAllowedMessage, null, this.COMPONENT_NAME, true);
          return;
        }
        console.log(`${this.COMPONENT_NAME}. removing machine: ${this.machine.type.toBold()}`);

        const modalRef = this.modalService.open(MachineDeleteComponent, {size: 'md'});

        modalRef.componentInstance.machineName = this.machine.type;

        modalRef.result.then((userConfirmed) => {
          console.log('Closed with: ', userConfirmed);

          if (!userConfirmed) {
            return;
          }

          this.loadingIndicator.show(this.translate.instant('Deleting'));
          this.machineService.delete(this.machine.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.machineDeleteEvent.emit(this.machine);
              let message = this.translate.instant('Successfully scheduled task to delete machine %machine');
              message = message.replaceAll('%machine', this.machine.type.toBold());
              this.notification.success(message, this.COMPONENT_NAME, true);
              this.menuNotificationService.broadcast(new MenuNotification(OperationType.DELETE, String(this.machine.id), this.machine.name, ''));
              const favoriteNotification = new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, String(this.machine.id), '', '');
              favoriteNotification.isMachine = true;
              this.favoriteNotificationService.broadcast(favoriteNotification);
              this.loadingIndicator.hide();
            }, (error) => {
              this.loadingIndicator.hide();
              const message = this.generateDeleteErrorMessage(error);
              this.notification.error(message, error, this.COMPONENT_NAME, true);
            });
        });
      },error => {
        this.notification.error(this.errorGettingDataMessage, error, this.COMPONENT_NAME, true);
      });
  }

  public onListGateways(machine: Machine): void {
    if (!machine || !machine.id) {
      return;
    }
    this.machineService.machineIsAvailableForUser(machine.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAvailable: boolean) => {
        if (!isAvailable) {
          this.notification.error(this.accessToMachineNotAllowedMessage, null, this.COMPONENT_NAME, true);
          return;
        }

        const modalRef = this.modalService.open(GatewayComponent, {size: 'lg'});
        console.log(this.machine);
        modalRef.componentInstance.machineId = this.machine.id;
        modalRef.componentInstance.tenantId = this.machine.parentId;
        modalRef.result.then();
      }, error => {
        this.notification.error(this.errorGettingDataMessage, error, this.COMPONENT_NAME, true);
      });
  }

  public onEditMachine(machine: Machine): void {
    if (!machine || !machine.id) {
      return;
    }

    this.machineService.machineIsAvailableForUser(machine.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAvailable: boolean) => {
        if (!isAvailable) {
          this.notification.error(this.accessToMachineNotAllowedMessage, null, this.COMPONENT_NAME, true);
          return;
        }
        const machineBackup = cloneDeep(this.machine);
        const modalRef = this.modalService.open(MachineEditComponent, {size: 'lg'});
        modalRef.componentInstance.firebaseUserId = this.firebaseUserId;
        modalRef.componentInstance.loggedUser = this.loggedUser;
        modalRef.componentInstance.machine = this.machine;
        modalRef.componentInstance.userRole = this.userRole;
        modalRef.result.then((result) => {
          this.machineUpdatedEvent.emit(result);
        }, (reason) => {
          if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE || reason === ModalDialogDismissOption.BACKDROP) {
            this.machineUpdatedEvent.emit(machineBackup);
          }
        });
    },error => {
      this.notification.error(this.errorGettingDataMessage, error, this.COMPONENT_NAME, true);
    });

  }

  public onEditMachineConfiguration(machine: Machine): void {
    if (!machine || !machine.id) {
      return;
    }

    this.loadingIndicator.show();
    this.machineService.machineIsAvailableForUser(machine.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAvailable: boolean) => {
        if (!isAvailable) {
          this.notification.error(this.accessToMachineNotAllowedMessage, null, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
          return;
        }
        const parameters = {
          id: machine.id,
          userRole : this.userRole,
          tenant: machine.tenant
        };
        const encodedParams = btou(JSON.stringify(parameters));
        this.router.navigate(
          ['machines', encodedParams],
          {state: {search: this.searchTerm}}
        );

        this.loadingIndicator.hide();
      }, (error) => {
        this.loadingIndicator.hide();
        this.notification.error(this.errorGettingDataMessage, error, this.COMPONENT_NAME, true);
      });
  }

  public isAdminOrHigher(): boolean {
    return (this.userRole.isAdminRole || this.userRole.isTechAdminRole || this.userRole.isSuperAdminRole);
  }

  private generateDeleteErrorMessage(error: any): string {
    const status = error.status;
    let message;
    switch (status) {
      case this.DENY_MACHINE_DELETION_HAS_GATEWAYS: {
        message = this.translate.instant('Error removing machine %machine. Delete associated gateways first');
        message = message.replaceAll('%machine', this.machine.type.toBold());
        break;
      }
      case this.DENY_MACHINE_DELETION_IS_TEMPLATE_MACHINE: {
        message = this.translate.instant('Error removing machine %machine. This is the template machine of %category and %tag tag. Before deleting set another machine as template.');
        message = message.replaceAll('%machine', this.machine.type.toBold());
        message = message.replaceAll('%category', this.machine.category.toBold());
        message = message.replaceAll('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');

        break;
      }
      default: {
        message = this.translate.instant('Error removing machine %machine.');
        message = message.replaceAll('%machine', this.machine.type.toBold());
        break;
      }
    }
    return message;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}

